// Centralized theme configuration for the entire application

// Reading-specific themes
export const readingColors = {
  'personal-pathway': {
    primary: 'hsl(var(--reading-numerology))',
    gradient: 'from-purple-900 via-purple-600 to-purple-300',
    border: 'border-reading-numerology',
  },
  'cosmic-profile': {
    primary: 'hsl(var(--reading-astrology))',
    gradient: 'from-blue-900 via-blue-600 to-blue-300',
    border: 'border-reading-astrology',
  },
  'hand-insight': {
    primary: 'hsl(var(--reading-palmistry))',
    gradient: 'from-amber-900 via-amber-600 to-amber-300',
    border: 'border-reading-palmistry',
  },
  'natural-flow': {
    primary: 'hsl(var(--reading-bazi))',
    gradient: 'from-red-900 via-red-600 to-red-300',
    border: 'border-reading-bazi',
  },
  'soul-journey': {
    primary: 'hsl(var(--reading-tarot))',
    gradient: 'from-indigo-900 via-indigo-600 to-indigo-300',
    border: 'border-reading-tarot',
  },
  'energy-mastery': {
    primary: 'hsl(var(--reading-numerology))',
    gradient: 'from-purple-900 via-purple-600 to-purple-300',
    border: 'border-reading-numerology',
  },
  'love-alchemy': {
    primary: 'hsl(var(--reading-palmistry))',
    gradient: 'from-pink-900 via-pink-600 to-pink-300',
    border: 'border-reading-palmistry',
  },
  'professional-awakening': {
    primary: 'hsl(var(--reading-astrology))',
    gradient: 'from-blue-900 via-blue-600 to-blue-300',
    border: 'border-reading-astrology',
  },
  'intuitive-messages': {
    primary: 'hsl(var(--reading-tarot))',
    gradient: 'from-indigo-900 via-indigo-600 to-indigo-300',
    border: 'border-reading-tarot',
  },
  'soul-illumination': {
    primary: 'hsl(var(--reading-numerology))',
    gradient: 'from-purple-900 via-purple-600 to-purple-300',
    border: 'border-reading-numerology',
  },
} as const;

// UI Component themes - centralized styling
export const componentThemes = {
  dialog: {
    overlay: 'bg-black/80',
    content: 'bg-background',
    border: 'border',
    closeButton: 'ring-offset-background data-[state=open]:bg-accent data-[state=open]:text-muted-foreground',
  },
  input: {
    background: 'bg-background',
    border: 'border-input',
    text: 'text-foreground',
    placeholder: 'placeholder:text-gray-500',
    focus: 'focus:border-primary focus:shadow-[0_4px_16px_rgba(107,70,193,0.15)]',
  },
  popover: {
    background: 'bg-popover',
    text: 'text-popover-foreground',
    border: 'border',
  },
  label: {
    text: 'text-gray-900',
  },
  button: {
    default: {
      background: 'bg-primary',
      text: 'text-primary-foreground',
      hover: 'hover:bg-primary/90',
    },
    outline: {
      background: 'bg-card',
      text: 'text-foreground',
      border: 'border-input',
      hover: 'hover:bg-muted',
    },
    navOutline: {
      background: 'bg-white/10',
      text: 'text-nav-foreground',
      border: 'border-white/20',
      hover: 'hover:bg-white/20',
    },
    ghost: {
      background: 'bg-transparent',
      text: 'text-foreground',
      hover: 'hover:bg-primary/5',
    },
    navGhost: {
      background: 'bg-transparent',
      text: 'text-nav-foreground',
      hover: 'hover:bg-white/10',
    },
    secondary: {
      background: 'bg-secondary',
      text: 'text-secondary-foreground',
      hover: 'hover:bg-secondary/80',
    },
    destructive: {
      background: 'bg-destructive',
      text: 'text-destructive-foreground',
      hover: 'hover:bg-destructive/90',
    },
    link: {
      text: 'text-primary',
      decoration: 'underline-offset-4 hover:underline',
    },
  },
  switch: {
    root: {
      checked: 'data-[state=checked]:bg-primary',
      unchecked: 'data-[state=unchecked]:bg-input',
      border: 'border-transparent',
      focus: 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    },
    thumb: {
      background: 'bg-card',
      shadow: 'shadow-lg',
      transition: 'transition-transform data-[state=checked]:translate-x-5 data-[state=unchecked]:translate-x-0',
    },
  },
  readingForm: {
    // Card styling
    card: {
      background: 'bg-white',
      darkBackground: 'dark:bg-white',
    },
    
    // Header elements
    header: {
      title: 'text-gray-900',           // Dark, bold titles
      subtitle: 'text-gray-700',         // Slightly lighter subtitles
      icon: 'text-primary',              // Accent color for icons
    },
    
    // Form labels and text
    text: {
      label: 'text-gray-900',            // Form field labels
      body: 'text-gray-800',             // General body text
      description: 'text-gray-700',      // Field descriptions
      muted: 'text-gray-600',            // Helper text, hints, character counts
      storyHook: 'text-gray-600',        // Story hook text
    },
    
    // Accent sections (story hooks, repeat reading, etc.)
    accent: {
      background: 'bg-primary/5',        // Light purple tint
      border: 'border-primary/10',       // Subtle purple border
    },
    
    // Input backgrounds
    input: {
      background: 'bg-white',
    },
    
    // Spacing and layout
    spacing: {
      sectionGap: 'space-y-6',           // Gap between form sections
      fieldGap: 'space-y-2',             // Gap within a field (label + input)
      padding: {
        accent: 'p-4',                   // Padding for accent sections
      },
    },
    
    // Typography
    typography: {
      title: 'text-3xl font-bold',
      subtitle: 'text-lg',
      label: 'text-base font-medium',
      helper: 'text-sm',
      small: 'text-xs',
    },
  },
} as const;

// Spacing configuration for consistent sizing
export const spacing = {
  button: {
    primary: 'h-14 px-8',        // 56px height (WCAG compliant)
    secondary: 'h-10 px-6',      // 40px height
    small: 'h-9 px-4',          // 36px height
  },
  input: {
    default: 'h-12 px-4 py-3',  // 48px height
    small: 'h-10 px-3 py-2',    // 40px height
  },
  card: {
    mobile: 'p-4',
    tablet: 'p-6',
    desktop: 'p-8',
  },
  touchTarget: 'min-h-[44px]',  // Minimum WCAG AA compliant
} as const;

// Typography scale - responsive and accessible
export const typography = {
  hero: {
    primary: 'text-6xl md:text-7xl',      // 60px -> 72px
    secondary: 'text-4xl md:text-5xl',    // 36px -> 48px
  },
  pageTitle: 'text-4xl md:text-5xl',
  sectionTitle: 'text-3xl md:text-4xl',
  cardTitle: 'text-xl md:text-2xl',
  body: {
    large: 'text-lg leading-relaxed',      // 18px, 1.625 line-height
    default: 'text-base leading-relaxed',  // 16px, 1.625 line-height
  },
  label: 'text-base font-medium',          // Increased from text-sm
  helper: 'text-sm leading-normal',
  sizes: {
    xs: 'text-xs',
    sm: 'text-sm',
    base: 'text-base',
    lg: 'text-lg',
    xl: 'text-xl',
    '2xl': 'text-2xl',
    '3xl': 'text-3xl',
  },
  weights: {
    normal: 'font-normal',
    medium: 'font-medium',
    semibold: 'font-semibold',
    bold: 'font-bold',
  },
} as const;

// Theme utility functions
export const getReadingTheme = (readingType: string) => {
  return readingColors[readingType as keyof typeof readingColors] || readingColors['personal-pathway'];
};

export const getComponentTheme = <T extends keyof typeof componentThemes>(
  componentName: T
): typeof componentThemes[T] => {
  return componentThemes[componentName];
};

export const getFontSize = (size: keyof typeof typography.sizes) => {
  return typography.sizes[size];
};

export const getFontWeight = (weight: keyof typeof typography.weights) => {
  return typography.weights[weight];
};

export const getReadingFormTheme = () => {
  return componentThemes.readingForm;
};
